import React, { useEffect, useMemo, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { PlainTextHeader } from 'Deer/components/HeaderComponents/PlainTextHeader';
import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { IntentDropdown } from 'Deer/intent/Reviews/components/IntentDropdown';
import { useSelectReviewsSelectedIntent } from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
import { Layout } from 'Deer/intent/components/Layout/Layout';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { setSelectedView, useSelectedView } from 'reducers/viewsSlice';
import useDeerL1PageBasedL3Routes from 'screens/performanceReview/hooks/useDeerL1PageBasedL3Routes';
import { ReviewDetailedReportGrid } from 'screens/performanceReview/manageAndInsights/ReviewDetailedReport/ReviewDetailedReportGrid';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { getPersistenceKey } from 'constants/helper';
import { sendPageViewEvent } from 'constants/mixpanelHelper';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';

interface SelectedIntent {
  id?: string;
  label?: string;
}

const COMPANY_COHORT = { id: -1, name: 'My Company', type: 'ALL_EMPLOYEE' };

const CompanyReviewDetailedReport = ({ headerLabel }: { headerLabel: string }) => {
  const { l3Options: tabsDeer } = useDeerL1PageBasedL3Routes();
  const { moduleName } = useOrganisationSettings();
  const gridRef = useRef();
  const { label } = (useSelectReviewsSelectedIntent() as SelectedIntent) ?? {};
  const dispatch = useDispatch();

  const cohortDetails = useSelectedView();
  /* Grid State persistence Key */
  const viewId = cohortDetails?.id;

  useEffect(() => {
    dispatch(setSelectedView(COMPANY_COHORT));

    return () => {
      dispatch(setSelectedView({}));
    };
  }, [dispatch]);

  const gridStatePersistenceKey = useMemo(() => {
    const tabId = checkIntentLandingPagesFeatureEnabled() ? label : 'Explore';
    const uniqueId = 'ReviewDetailedReportGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, [label, moduleName, viewId]);

  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName) ?? [];
  useEffect(() => {
    sendPageViewEvent({
      eventProperties: {
        page: 'Company-reviews-detailed-report',
      },
    });
  }, []);
  if (checkIntentLandingPagesFeatureEnabled()) {
    return (
      <Layout
        Filters={
          <IntentDropdown
            showKebabMenu
            gridRef={gridRef}
            breadcrumbs={{
              items: [
                {
                  label: 'Company',
                  to: applicableL2CompanyModules[0]?.value,
                },
              ],
              page: {
                label: 'Reviews',
                to: '',
              },
            }}
          />
        }
      >
        <ReviewDetailedReportGrid
          gridRef={gridRef}
          cohortDetails={cohortDetails}
          gridStatePersistenceKey={gridStatePersistenceKey}
        />
      </Layout>
    );
  }

  return (
    <>
      <Box pb={1}>
        <PlainTextHeader>{headerLabel}</PlainTextHeader>

        <PageTabbedNavigationV1 tabs={tabsDeer} />
      </Box>
      <ReviewDetailedReportGrid
        gridRef={gridRef}
        cohortDetails={cohortDetails}
        gridStatePersistenceKey={gridStatePersistenceKey}
      />
    </>
  );
};

export { CompanyReviewDetailedReport };
